import React  from 'react';
import useDeferredCollection from '@utilities/hooks/useDeferredCollection/useDeferredCollection';
import { ApiActionPayload } from '@interfaces/ApiAction/ApiActionPayload';

/***
 * useCollection
 * @description
 * Retrieve full list of entities at collection endpoint. Will cancel update if updateCollection (or, because of
 * updateCollection dependencies, collectionEndpoint) is updated.
 *
 * @param collectionEndpoint {string}
 *  - ideally a non changing endpoint but will also work with endpoints that require an ID.
 *  - use the endpoint constant
 *
 * @param overrides {Partial<ApiActionPayload<EntityType[]>>} - optional overrides for the API call
 * @example
 * 	const units = useCollection<Unit>(ENDPOINT_USER_UNITS);
 *
 * 			<Dropdown value={selectedUnitId} select={setSelectedUnitId}>
 * 				{units.map(({ id, hospitalName, name }) =>
 * 					<MenuItem key={id} value={id}>{hospitalName}, {name}</MenuItem>
 * 				)}
 * 			</Dropdown>
 */
const useCollection = <EntityType extends object>(collectionEndpoint: string, overrides?: Pick<ApiActionPayload<EntityType[]>, 'shouldDisableErrorHandler' | 'shouldDisableLoadIndicator'>): EntityType[] => {
	const [collection, updateCollection] = useDeferredCollection<EntityType>(collectionEndpoint, overrides);

	React.useEffect(() => {
		const abortController = updateCollection();
		return () => abortController.abort();
	}, [updateCollection]);

	return collection;
};

export default useCollection;
